import { Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { DEFAULT_ERROR_TEXT, PHONE_NUMBER_MASK } from '../../../../constants';
import { CustomerService } from '../../../../services/customer.service';
import { HeadService } from '../../../../services/head.service';
import { LanguageService } from '../../../../services/language/language.service';
import { ContactInfoComponent } from '../../../../components/contact-info/contact-info.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxMaskPipe } from 'ngx-mask';
import { ContactInfoDto } from '../../../../dtos/contact-info.dto';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ButtonComponent } from '../../../../components/button/button.component';
import { UpdateFullNameClientDto } from '../../../../dtos/update-full-name-client.dto';
import { UpdatePhoneNumberClientDto } from '../../../../dtos/update-phone-number.client.dto';
import { MatDialog } from '@angular/material/dialog';
import { OtpModalComponent, OtpModalData } from '../../../../components/otp-modal/otp-modal.component';
import { UpdateEmailClientDto } from '../../../../dtos/update-email.client.dto';
import { ModalResult } from '../../../../interfaces/modal-result.interface';
import { ResetPasswordModalComponent } from '../reset-password-modal/reset-password-modal.component';


@Component({
  selector: 'account-customer-info',
  templateUrl: './account-customer-info.component.html',
  styleUrls: ['./account-customer-info.component.scss'],
  imports: [ContactInfoComponent, ReactiveFormsModule, TranslatePipe, NgxMaskPipe, ButtonComponent],
})
export class AccountCustomerInfoComponent implements OnInit {
  private customerService = inject(CustomerService);
  private headService = inject(HeadService);
  private languageService = inject(LanguageService);
  private destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  formVisibility = signal<'contact' | 'phone' | 'email' | null>(null);
  private isFormErrorsVisible = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  httpError = signal<string>(null);

  isEditBtnDisabled = computed<boolean>(() => {
    return this.formVisibility() !== null || this.isLoading();
  });

  emailControl = new FormControl<string>('', Validators.required);

  readonly contactInfoCmp = viewChild(ContactInfoComponent);

  get customer() { return this.customerService.customer; }

  ngOnInit(): void {
    this.emailControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isFormErrorsVisible.set(false);
        this.httpError.set(null);
      })

    this.setMeta();
  }

  saveContactInfo(): void {
    if (this.isLoading() || !this.contactInfoCmp().checkValidity()) {
      return;
    }

    const contactInfo: ContactInfoDto = this.contactInfoCmp().getValue();
    const dto: UpdateFullNameClientDto = {
      firstName: contactInfo.firstName,
      lastName: contactInfo.lastName,
      middleName: contactInfo.middleName,
    };

    this.isLoading.set(true);
    this.customerService.updateFullName(dto)
      .pipe(

        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.setFormVisibility(null),
        error: error => this.httpError.set(error.error?.message || DEFAULT_ERROR_TEXT),
      })
  }

  savePhoneNumber(): void {
    if (this.isLoading() || !this.contactInfoCmp().checkValidity()) {
      return;
    }

    const contactInfo: ContactInfoDto = this.contactInfoCmp().getValue();
    if (contactInfo.phoneNumber === this.customer.contactInfo.phoneNumber) {
      this.setFormVisibility(null);
      return;
    }

    const dto: UpdatePhoneNumberClientDto = {
      phoneNumber: contactInfo.phoneNumber,
    };

    this.isLoading.set(true);
    this.customerService.updatePhoneNumberInit(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        switchMap(response => {
          const otpModalData: OtpModalData = {
            otpRequest: response.data,
            onSubmit: otpConfirmDto => this.customerService.updatePhoneNumberConfirm(otpConfirmDto),
            onResendOtp: () => this.customerService.updatePhoneNumberInit(dto).pipe(map(res => res.data)),
            successMessageKey: 'account_customer_info.success_phone_change',
          };
          return this.dialog.open(OtpModalComponent, { data: otpModalData }).afterClosed();
        }),
        filter((modalResult: ModalResult) => modalResult.type === 'CONFIRM'),
        tap(() => this.setFormVisibility(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: error => this.httpError.set(error.error?.message || DEFAULT_ERROR_TEXT),
      });
  }

  saveEmail(): void {
    if (this.isLoading()) {
      return;
    }

    this.isFormErrorsVisible.set(false);
    if (this.emailControl.invalid) {
      this.isFormErrorsVisible.set(true);
      return;
    }

    const email = this.emailControl.getRawValue();
    if (email === this.customer.contactInfo.email) {
      this.setFormVisibility(null);
      return;
    }

    const dto: UpdateEmailClientDto = {
      email: email,
    };

    this.isLoading.set(true);
    this.customerService.updateEmailInit(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        switchMap(response => {
          const otpModalData: OtpModalData = {
            otpRequest: response.data,
            onSubmit: otpConfirmDto => this.customerService.updateEmailConfirm(otpConfirmDto),
            onResendOtp: () => this.customerService.updateEmailInit(dto).pipe(map(res => res.data)),
            successMessageKey: 'account_customer_info.success_email_change',
          };
          return this.dialog.open(OtpModalComponent, { data: otpModalData }).afterClosed();
        }),
        filter((modalResult: ModalResult) => modalResult.type === 'CONFIRM'),
        tap(() => this.setFormVisibility(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: error => this.httpError.set(error.error?.message || DEFAULT_ERROR_TEXT),
      });
  }

  isControlInvalid(control: AbstractControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  setFormVisibility(formType: 'contact' | 'phone' | 'email' | null): void {
    this.isFormErrorsVisible.set(false);
    this.httpError.set(null);
    if (formType === 'email') {
      this.emailControl.setValue(this.customer.contactInfo.email);
    }

    this.formVisibility.set(formType);
  }

  changePassword(): void {
    this.dialog.open(ResetPasswordModalComponent);
  }

  private setMeta() {
    this.languageService.getTranslation('account_customer_info.account').subscribe(text => {
      this.headService.setMeta({ title: text, description: text });
    });
  }

  protected readonly PHONE_NUMBER_MASK = PHONE_NUMBER_MASK;
}
